import { useState, useEffect, useCallback, useRef } from 'react';
import { listServices, ServiceMetadata } from '../api/services';

const DEFAULT_PAGE_SIZE = 20;

interface UseServicesOptions {
  enabled?: boolean;
  pageSize?: number;
}

interface UseServicesResult {
  services: ServiceMetadata[];
  isLoading: boolean;
  error: string | null;
  page: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  nextPage: () => void;
  previousPage: () => void;
  refresh: () => void;
}

export function useServices(
  serviceNameSearchTerm?: string,
  { enabled = true, pageSize = DEFAULT_PAGE_SIZE }: UseServicesOptions = {},
): UseServicesResult {
  const normalizedSearchTerm = serviceNameSearchTerm?.trim() || undefined;
  const [services, setServices] = useState<ServiceMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const pageTokensRef = useRef<Array<string | undefined>>([undefined]);
  const fetchIdRef = useRef(0);

  const fetchPage = useCallback(
    async (pageToken?: string) => {
      const currentFetchId = ++fetchIdRef.current;
      setIsLoading(true);
      setError(null);

      try {
        const response = await listServices(pageToken, pageSize, normalizedSearchTerm);

        if (fetchIdRef.current !== currentFetchId) return;

        setServices(response.services);
        setNextToken(response.nextToken);
      } catch (err) {
        if (fetchIdRef.current !== currentFetchId) return;
        setError(err instanceof Error ? err.message : 'Failed to load services');
      } finally {
        if (fetchIdRef.current === currentFetchId) {
          setIsLoading(false);
        }
      }
    },
    [normalizedSearchTerm, pageSize],
  );

  useEffect(() => {
    fetchIdRef.current += 1;
    pageTokensRef.current = [undefined];
    setPageIndex(0);
    setNextToken(undefined);
    setError(null);
    setServices([]);

    if (!enabled) {
      setIsLoading(false);
      return;
    }

    fetchPage();
  }, [enabled, fetchPage]);

  const nextPage = useCallback(() => {
    if (!nextToken || isLoading) return;

    const nextPageIndex = pageIndex + 1;
    pageTokensRef.current = pageTokensRef.current.slice(0, nextPageIndex);
    pageTokensRef.current[nextPageIndex] = nextToken;
    setPageIndex(nextPageIndex);
    setServices([]);
    fetchPage(nextToken);
  }, [fetchPage, isLoading, nextToken, pageIndex]);

  const previousPage = useCallback(() => {
    if (pageIndex === 0 || isLoading) return;

    const previousPageIndex = pageIndex - 1;
    setPageIndex(previousPageIndex);
    setServices([]);
    fetchPage(pageTokensRef.current[previousPageIndex]);
  }, [fetchPage, isLoading, pageIndex]);

  const refresh = useCallback(() => {
    if (!enabled) return;
    fetchPage(pageTokensRef.current[pageIndex]);
  }, [enabled, fetchPage, pageIndex]);

  return {
    services,
    isLoading,
    error,
    page: pageIndex + 1,
    hasNextPage: Boolean(nextToken),
    hasPreviousPage: pageIndex > 0,
    nextPage,
    previousPage,
    refresh,
  };
}
